<template>
  <div>
    <ModalBase :show-modal="true" @close-modal="close" auto-h width-md :overlay-scrollb="false" class="team-modal">
      <template v-slot:titlebar>
        <div class="title-connection-card-wrap">
          <div class="connection-card">
            <div class="connection-card-userinfo">
              <div v-if="!isPending" class="connection-card__avatar">
                <Avatar :user-id="teamMember.id" tooltip-disabled />
              </div>
              <div v-else class="connection-card__avatar">
                <Avatar defaultAvatar tooltip-disabled />
              </div>
              <div class="connection-card__info">
                <div class="connection-card__info--name">{{ memberName }}</div>
                <span class="connection-card__info--email">{{ memberEmail }}</span>
              </div>
            </div>
          </div>
          <div v-if="isPending" class="pending-invitation">
            <div class="text-warning">{{ $gettext('Pending invitation') }}</div>
            <div>
              {{ $gettext('Sent on') }} {{ teamMember.created | formatDate }}
              <font-awesome-icon id="tooltip-team-invite-companies" class="text-info tooltip-icon tooltip-icon--question" icon="fa-solid fa-circle-question" />
              <b-tooltip target="tooltip-team-invite-companies" triggers="hover">{{ $gettext('You can add this user to other companies only after he accepts the invitation.') }}</b-tooltip>
            </div>
          </div>
          <div v-if="isInactive" class="pending-invitation">
            <div class="text-warning">{{ $gettext('Inactive user') }}</div>
            <div>
              {{ $gettext('Reconnect or delete this user') }} {{ teamMember.created | formatDate }}
              <font-awesome-icon id="tooltip-team-inactive-user" class="text-info tooltip-icon tooltip-icon--question" icon="fa-solid fa-circle-question" />
              <b-tooltip target="tooltip-team-inactive-user" triggers="hover">{{ $gettext('To completely remove this user, first you need to delete all company connections') }}</b-tooltip>
            </div>
          </div>
        </div>
      </template>
      <template v-slot:maincontent>
        
        <b-form>
          <b-form-row>
            <b-col cols="12">
              <h4>{{ $gettext('Has access to') }}</h4>
              <b-table
                :class="['team-rights-table gk-table gk-table--responsive gk-table--headed gk-table--hover']"
                responsive
                :items="connectionsList"
                :fields="rightsFields"
                v-if="!mobile_app_view"
              >
                <template #head()="row">
                  <div v-if="row.field.key === 'company_profile.name'">
                    {{ row.field.label }}
                  </div>
                  <div v-else-if="row.field.key === 'actions'" style="text-align: center;">
                    {{ $gettext('Actions') }}
                  </div>
                  <div v-else class="col-th-right">
                    <font-awesome-icon :id="'tooltip-' + row.field.key" :icon="row.field.icon" v-b-tooltip.hover />
                    <b-tooltip :target="'tooltip-' + row.field.key" triggers="hover" boundary="viewport">{{ row.field.tooltip }}</b-tooltip>
                  </div>
                </template>
                <template #cell()="row">
                  <div v-if="row.field.key === 'company_profile.name'">
                    <div class="status-icon">
                      <div v-if="connectionsList[row.index].rights.admin">
                        <font-awesome-icon :id="'tooltip-admin-conn-' + row.index" icon="fa-solid fa-sitemap" class="text-blue" />
                        <b-tooltip :target="'tooltip-admin-conn-' + row.index" triggers="hover" boundary="viewport">{{ $gettext('This company belongs to you') }}</b-tooltip>
                      </div>
                      <div v-else-if="(!isPending && !connectionsList[row.index].is_active)">
                        <font-awesome-icon :id="'tooltip-inactive-conn-' + row.index" icon="fa-solid fa-warning" class="text-warning" />
                        <b-tooltip :target="'tooltip-inactive-conn-' + row.index" triggers="hover" boundary="viewport">{{ $gettext('This connection is inactive') }}</b-tooltip>
                      </div>
                      <div v-else-if="isPending">
                        <font-awesome-icon :id="'tooltip-pending-conn-' + row.index" icon="fa-regular fa-clock" class="text-warning" />
                        <b-tooltip :target="'tooltip-pending-conn-' + row.index" triggers="hover" boundary="viewport">{{ $gettext('Pending connection') }}</b-tooltip>
                      </div>
                      <div v-else>
                        <font-awesome-icon :id="'tooltip-active-conn-' + row.index" icon="fa-regular fa-circle-check" class="text-brand" />
                        <b-tooltip :target="'tooltip-active-conn-' + row.index" triggers="hover" boundary="viewport">{{ $gettext('Active connection') }}</b-tooltip>
                      </div>
                    </div>
                    <span>{{ row.value }}</span>
                  </div>
                  <div v-else-if="row.field.key === 'actions'" class="col-actions">
                    <GekkoButton
                      @click="reactivateConnection(connectionsList[row.index].id)"
                      :id="'action-reconnect-' + row.index"
                      :disabled="isPending || (!isPending && connectionsList[row.index].is_active) || isFrozen"
                      icon="fa-solid fa-arrow-right-arrow-left"
                      variant="link"
                      class="text-blue"
                      size="sm"
                    />
                    <b-tooltip :target="'action-reconnect-' + row.index" triggers="hover" boundary="viewport">{{ $gettext('Reactivate connection') }}</b-tooltip>
                    <GekkoButton 
                      @click="deleteConnection(row.index)"
                      :id="'action-delete-' + row.index" 
                      :disabled="isPending"
                      icon="fa-solid fa-trash-can"
                      variant="link"
                      class="text-danger"
                      size="sm"
                    />
                    <b-tooltip :target="'action-delete-' + row.index" triggers="hover" boundary="viewport">{{ $gettext('Delete connection') }}</b-tooltip>
                  </div>
                  <div v-else class="col-right-check" :class="{ disabled: (!isPending && !connectionsList[row.index].is_active) }">
                    <b-form-checkbox-group v-model="connectionsList[row.index].selectedRights" :disabled="(!isPending && !connectionsList[row.index].is_active)">
                      <b-form-checkbox :value="row.field.value" class="mr-0" style="margin-left: 7px;" />
                    </b-form-checkbox-group>
                  </div>
                </template>
              </b-table>

              <div v-else>

                <div v-for="(connection, conn_index) in connectionsList" :key="connection.id" class="conn-collapse-item">
                  <div class="conn-collapse-button-wrap">
                    <div class="conn-collapse-button">
                      <div class="status-icon">
                        <div v-if="connection.rights.admin">
                          <font-awesome-icon :id="'tooltip-admin-conn-' + conn_index" icon="fa-solid fa-sitemap" class="text-blue" />
                          <b-tooltip :target="'tooltip-admin-conn-' + conn_index" triggers="hover">{{ $gettext('This company belongs to you') }}</b-tooltip>
                        </div>
                        <div v-else-if="(!isPending && !connection.is_active)">
                          <font-awesome-icon :id="'tooltip-inactive-conn-' + conn_index" icon="fa-solid fa-warning" class="text-warning" />
                          <b-tooltip :target="'tooltip-inactive-conn-' + conn_index" triggers="hover">{{ $gettext('This connection is inactive') }}</b-tooltip>
                        </div>
                        <div v-else>
                          <font-awesome-icon :id="'tooltip-active-conn-' + conn_index" icon="fa-regular fa-circle-check" class="text-brand" />
                          <b-tooltip :target="'tooltip-active-conn-' + conn_index" triggers="hover">{{ $gettext('Active connection') }}</b-tooltip>
                        </div>
                      </div>
                      <div class="text-brand" style="flex: 1;" v-b-toggle="'collapse-conn-' + connection.id">
                        {{ connection.company_profile.name }}
                      </div>
                    </div>
                    <div class="col-actions">
                      <GekkoButton
                        @click="reactivateConnection(connection.id)"
                        :id="'action-reconnect-' + conn_index" 
                        :disabled="isPending || (!isPending && connection.is_active) || isFrozen"
                        icon="fa-solid fa-arrow-right-arrow-left"
                        variant="link"
                        class="text-blue"
                        size="sm"
                      />
                      <b-tooltip :target="'action-reconnect-' + conn_index" triggers="hover">{{ $gettext('Reactivate connection') }}</b-tooltip>
                      <GekkoButton 
                        @click="deleteConnection(conn_index)"
                        :id="'action-delete-' + conn_index" 
                        :disabled="isPending"
                        icon="fa-solid fa-trash-can"
                        variant="link"
                        class="text-danger"
                        size="sm"
                      />
                      <b-tooltip :target="'action-delete-' + conn_index" triggers="hover">{{ $gettext('Delete connection') }}</b-tooltip>
                    </div>
                  </div>
                  
                  <b-collapse :id="'collapse-conn-' + connection.id" :ref="'collapse-conn-' + connection.id">
                    <b-card class="gk-card">
                      <b-form-group :label="$gettext('User Rights')" label-class="gk-label-margin gk-modal-label" class="mb-0" v-slot="{ ariaDescribedby }">
                        <b-form-checkbox-group
                          v-model="connection.selectedRights"
                          :options="selectRightsOptions"
                          :aria-describedby="ariaDescribedby"
                          name="user-rights"
                          class="team-modal-user-rights"
                          stacked
                        />
                      </b-form-group>
                    </b-card>
                  </b-collapse>
                </div>
                
              </div>

              <div v-if="!isPending && (availableCompaniesLeft.length > 0) && !isFrozen" style="margin-top: 1rem;">
                <h4>{{$gettext('Add access to')}}</h4>
                <b-form-row>
                  <b-col cols="12" lg="6">
                    <b-form-group id="input-group-4">
                      <b-form-select 
                        id="input-4" value-field="id" text-field="name"
                        v-model="companySelectValue"
                        :options="[{ id: null, name: $gettext('Select a company'), disabled: true }, ...availableCompaniesLeft]"
                        @change="addRightsRow"
                      />
                    </b-form-group>
                  </b-col>
                </b-form-row>
              </div>
            </b-col>
          </b-form-row>
        </b-form>
      </template>

      <template v-slot:actions>
        <b-alert :show="isFrozen" class="gk-alert mb-0" variant="warning">
          <div class="d-flex justify-content-between align-items-center">
            {{ $gettext('You cannot reconnect or update this user unless you purchase an additional team slot.') }}
            <GekkoButton 
              @click="updateTeamSize()" 
              icon="fa-solid fa-plus"
              :text="$gettext('Purchase team slot')" 
            />
          </div>
        </b-alert>
        <div v-if="isPending" class="d-flex justify-content-between">
          <GekkoButton 
            @click="deleteInvitation()" 
            icon="fa-solid fa-user-xmark"
            :text="$gettext('Delete invitation')" 
            variant="link"
            class="text-danger"
          />
          <div>
            <GekkoButton
              icon="fa-solid fa-paper-plane"
              :text="$gettext('Resend')"
              @click="resendInvitation()"
              variant="link"
            />
            <GekkoButton
              @click="saveInvitationChanges()"
              icon="fa-solid fa-save"
              :text="$gettext('Save changes')"
              class="ml-2"
            />
          </div>
        </div>
        <div v-else-if="!isFrozen" class="d-flex justify-content-between">
          <GekkoButton
              @click="saveRightsChanges()"
              icon="fa-solid fa-save"
              :text="$gettext('Save changes')"
              class="ml-auto"
            />
          </div>
      </template>
    </ModalBase>
  </div>
</template>

<script>
import { mapGetters } from "vuex"
import GekkoButton from '@/components/GekkoButton'
import ModalBase from "@/components/base_components/ModalBase"
import Avatar from "@/components/Avatar"

import CompanyOwnerService from "@/store/services/companyowner.service.js"
import UserInviteService from "@/store/services/userinvite.service"
import UserRightsService from "@/store/services/userrights.service"
import PlanService from "@/store/services/plan.service"

export default {
  name: 'TeamMemberModal',
  components: {
    GekkoButton,
    ModalBase,
    Avatar
  },
  props: {
    teamMember: {
      type: Object,
      default: () => {}
    },
    memberStatus: {
      type: String,
      default: null
    },
    openSlots: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      rightsFields: [
        { key: 'company_profile.name', label: this.$gettext('Company'), thClass: 'companyTh' },
        { key: 'rights.invoices', value: 'invoices',  label: '', icon: 'fa-solid fa-file', tooltip: this.$gettext('Create and send invoices') },
        { key: 'rights.costs', value: 'costs', label: '', icon: 'fa-solid fa-cart-shopping', tooltip: this.$gettext('Can add and manage costs') },
        { key: 'rights.hours', value: 'hours', label: '', icon: 'fa-regular fa-clock', tooltip: this.$gettext('Can track time') },
        { key: 'rights.trips', value: 'trips', label: '', icon: 'fa-solid fa-car', tooltip: this.$gettext('Can log trips') },
        { key: 'rights.banks', value: 'banks', label: '', icon: 'fa-solid fa-building-columns', tooltip: this.$gettext('Can see and manage bank transactions') },
        { key: 'rights.edit_company_profile', value: 'edit_company_profile', label: '', icon: 'fa-solid fa-briefcase', tooltip: this.$gettext('Can manage company (change details, connect accountant, download data)') },
        { key: 'rights.change_customers', value: 'change_customers', label: '', icon: 'fa-solid fa-user-group', tooltip: this.$gettext('Can see the customers page, create and change your customer/project details') },
        { key: 'rights.financial_details', value: 'financial_details', label: '', icon: 'fa-solid fa-chart-column', tooltip: this.$gettext('Can view financial reports')},
        { key: 'rights.access_company_wide_resources', value: 'access_company_wide_resources', label: '', icon: 'fa-solid fa-user-secret', tooltip: this.$gettext('Can also see information from other users') },
        'actions'
      ],
      invitation: {
        invite_from: null,
        invite_to_name: null,
        invite_to: null,
        message: null,
        rights: { // TODO implement this (currently hardcoded)
          hours: true,
          costs: true,
          invoices: true,
          trips: true,
          banks: true,
          edit_company_profile: true,
          change_customers: true,
          financial_details: true,
          access_company_wide_resources: true
        },
        company_profile: null,
      },
      connectionsList: [],
      showAddCompanyButton: false,
      companySelectValue: null,
      mobile_app_view: null,
      // mobile_app_view: true,
    }
  },
  computed: {
    ...mapGetters({ 
      availableCompanies: "team/getMyOwnCompanies",
      currentUser: 'main/getCurrentUser',
      getMobileAppView: "main/getMobileAppView"
    }),
    isPending () {
      return this.memberStatus === 'pending'
    },
    isInactive () {
      return this.memberStatus === 'inactive'
    },
    isFrozen () {
      return this.openSlots < 1 && this.isInactive
    },
    memberName () {
      return (this.isPending) ? this.teamMember.invite_to_name : this.teamMember.display_name
    },
    memberEmail () {
      return (this.isPending) ? this.teamMember.invite_to : this.teamMember.email
    },
    availableCompaniesLeft () {
      return this.availableCompanies.filter(company => !this.connectionsList.some(connected => company.id === connected.company_profile.id) )
    },
    selectRightsOptions () {
      return [
        { value: 'invoices',                      text: this.$gettext('Create and send invoices') },
        { value: 'costs',                         text: this.$gettext('Can add and manage costs') },
        { value: 'hours',                         text: this.$gettext('Can track time') },
        { value: 'trips',                         text: this.$gettext('Can log trips') },
        { value: 'banks',                         text: this.$gettext('Can see and manage bank transactions') },
        { value: 'edit_company_profile',          text: this.$gettext('Can manage company (change details, connect accountant, download data)') },
        { value: 'change_customers',              text: this.$gettext('Can see the customers page, create and change your customer/project details') },
        { value: 'financial_details',             text: this.$gettext('Can view financial reports') },
        { value: 'access_company_wide_resources', text: this.$gettext('Can also see information from other users') }
      ]
    }
  },
  created () {
    this.mobile_app_view = this.getMobileAppView
    document.body.classList.add('modal-on')
    this.setModalInitialData()
  },
  methods: {
    close () {
      this.$emit('close', false)
      document.body.classList.remove('modal-on')
    },
    setModalInitialData () {
      this.connectionsList = (this.isPending) ? [this.teamMember] : JSON.parse(JSON.stringify(this.teamMember.connections))

      // exclude connections from owned companies on users (except for current user)
      if (!this.isPending && this.teamMember.id !== this.currentUser.id) {
        this.connectionsList = this.connectionsList.filter(connection => !connection.rights.admin)
      }

      this.connectionsList.forEach(connection => {
        const conn_keys = Object.keys(connection.rights).filter(key => connection.rights[key] === true)
        this.$set(connection, 'selectedRights', conn_keys)
      })

      this.showAddCompanyButton = !this.isPending
      console.log('connectionsList', this.connectionsList)
    },
    saveInvitationChanges () {
      this.setConnectionSelectedRights(this.connectionsList[0])
      this.updateConnectionRights(this.connectionsList[0]).then(() => {
        this.$store.commit("setAlert", {
          title: this.$gettext("Team invitation"),
          text: this.$gettext("Updated invitation successfully"),
          variant: "success",
          duration: 4
        })
        this.close()
      })
    },
    setConnectionSelectedRights (connection) {
      for (const key in connection.rights) {
        if (key !== 'id') this.$set(connection.rights, key, connection.selectedRights.includes(key) ? true : false)
      }
    },
    saveRightsChanges () {
      const connection_promises = []

      this.connectionsList.forEach(connection => {
        this.setConnectionSelectedRights(connection)
        let connection_promise

        if (connection.rights.id) {
          if (connection.is_active) {
            connection_promise = this.updateConnectionRights(connection)
          }
        } else {
          connection_promise = this.createConnectionRights(connection)
        }
        
        connection_promises.push(connection_promise)
      })

      Promise.all(connection_promises).then((response) => {
        // all 'success' default
        let alert_title = this.$gettext('Connection update')
        let alert_txt = this.$gettext('All changes saved successfully')
        let alert_variant = 'success'
        
        // all 'error'
        if (response.every(result => result === 'error')) {
          alert_title = this.$gettext('Error')
          alert_txt = this.$gettext('An error occurred')
          alert_variant = 'danger'
        }

        // some 'success'
        if (response.some(result => result === 'permission')) {
          alert_title = this.$gettext('Permission required')
          alert_txt = this.$gettext('Some of those changes could not be applied')
          alert_variant = 'warning'
        }

        this.$store.commit('setAlert', { title: alert_title, text: alert_txt, variant: alert_variant, duration: 4 })
        this.$emit('refresh-data', false)
        this.close()
      })
    },
    deleteInvitation () {
      UserInviteService.delete(this.teamMember.id).then(() => {
        this.$store.commit("setAlert", {
          title: this.$gettext("Team invitation"),
          text: this.$gettext("Invitation has been deleted"),
          variant: "success",
          duration: 4
        });
        this.$emit('refresh-data', false)
        this.close()
      })
    },
    updateConnectionRights (connection) {
      return UserRightsService.update(connection.rights).then(() => {
        return 'success'
      }).catch((response) => {
        let error_status = 'error'
        if (response.status == 403) {
          error_status = 'permission'
        }
        return error_status
      })
    },
    createConnectionRights (connection) {
      const newCompanyOwner = {
        company_profile: connection.company_profile.id,
        is_active: true,
        rights: connection.rights,
        showRights: true,
        user: connection.user
      }
      return CompanyOwnerService.create(newCompanyOwner).then(() => {
        // console.log('company ownership created')
        return 'success'
      }).catch((response) => {
        const error_title = this.$gettext('Error')
        let error_txt = this.$gettext('An error occurred')
        let error_status = 'error'

        if (response.status == 400 && response.data[0] === 'Extra users must be purchased before activating the connection.') {
          error_txt = this.$gettext('Extra users must be purchased before activating the connection.')
        }
        if (response.status == 403) {
          error_txt = this.$gettext('You do not have access to do this operation.')
          error_status = 'permission'
        }
        this.$store.commit('setAlert', { title: error_title, text: error_txt, variant: 'danger', duration: 4 });
        return error_status
      })
    },
    reactivateConnection (connection_id) {
      CompanyOwnerService.reactivate(connection_id).then(() => {
        this.connectionsList.find(item => item.id === connection_id).is_active = true
        this.$emit('refresh-data', { modal_status: 'active' })
        this.$store.commit("setAlert", {
          title: this.$gettext("Team member connection"),
          text: this.$gettext("Successtully reactivated team member connection"),
          variant: "success",
          duration: 4
        });
      }).catch((response) => {
        const error_title = this.$gettext('Error')
        let error_txt = this.$gettext('An error occurred')
        if (response.status == 403) {
          if ( response.data === 'insufficient team open slots' ) {
            error_txt = this.$gettext('You do not have any available team slots.')
          } else {
            error_txt = this.$gettext('You do not have permission to do this operation.')
          }
        }
        this.$store.commit('setAlert', { title: error_title, text: error_txt, variant: 'danger', duration: 4 });
      })
    },
    deleteConnection (connection_index) {
      if (this.connectionsList[connection_index].rights.admin) {
        const error_title = this.$gettext('Could not delete')
        const error_txt = this.$gettext('You are the admin of this company and therefore you can not delete this connection. You can remove your company on the \'Company details\' page after you remove all the costs and invoices attached to it.')
        this.$store.commit('setAlert', { title: error_title, text: error_txt, variant: 'danger', duration: 4 });
      } else {
        if (this.connectionsList[connection_index].id) {
          CompanyOwnerService.destroy(this.connectionsList[connection_index].id).then(() => {
            
            let alert_title = this.$gettext('Team member access rights')
            let alert_text = this.$gettext('Removed user access to company')

            this.$emit('refresh-data', false)
            this.connectionsList.splice(connection_index, 1)
            if (this.connectionsList.length < 1) {
              alert_title = this.$gettext('Team member access rights')
              alert_text = this.$gettext('User connection removed completely')
              this.close()
            }

            this.$store.commit("setAlert", {
              title: alert_title,
              text: alert_text,
              variant: "success",
              duration: 4
            })
          }).catch((response) => {
            const error_title = this.$gettext('Error')
            let error_txt = this.$gettext('An error occurred')
            if (response.status == 403) {
              error_txt = this.$gettext('You do not have permission to do this operation.')
            }
            this.$store.commit('setAlert', { title: error_title, text: error_txt, variant: 'danger', duration: 4 });
          })
        } else {
          this.connectionsList.splice(connection_index, 1);
        }
      }
    },
    addRightsRow (company_id) {
      const newConnectionTo = {
        company_profile: this.availableCompanies.find(company => company.id === company_id),
        rights: this.invitation.rights,
        user: this.teamMember.id,
        selectedRights: Object.keys(this.invitation.rights).filter(key => this.invitation.rights[key] === true),
        is_active: true
      }
      this.connectionsList.push(newConnectionTo)
      this.companySelectValue = null
    },
    resendInvitation () {
      UserInviteService.resendInvitation(this.teamMember.invite_code).then(() => {
        this.$store.commit("setAlert", {
          title: this.$gettext("Team invitation"),
          text: this.$gettext("Re-sent invitation successfully"),
          variant: "success",
          duration: 4
        });
        this.close()
      })
    },
    updateTeamSize () { // just for 1 user
      return PlanService.addTeamMember({ team_members: 1 }).then((response) => {
        this.$router.push({ name: 'ConfirmPayment', params: { transactionId: response.data } });
      })
    },
  }
}
</script>

<style lang="scss">
.team-modal {

  .title-connection-card-wrap {
    display: flex;
    align-items: center;
    margin-bottom: 2rem;

    .pending-invitation {
      font-size: 14px;
      padding-left: 1rem;
      margin-left: 1rem;
      border-left: 1px solid $colorBorderDash;
    }
  }
  .connection-card {
    border: none;
    padding: 0 !important;
    width: auto;
    height: auto;
    .connection-card__info--name {
      font-size: 26px;
    }
    .connection-card__info--email {
      font-size: 16px;
    }
  }
  .team-modal-user-rights {
    font-size: 14px;
    .custom-control-label {
      padding-top: 2px;
    }
  }
  .team-rights-table {
    margin-bottom: 1rem !important;
    thead {
      .svg-inline--fa {
        color: $colorBlue;
        font-size: 18px;
        cursor: pointer;
      }
    }
    .companyTh { padding-left: calc(0.75rem + 1.5rem); }
    .status-icon { width: 1.5rem; display: inline-block; text-align: left; }
    .col-th-right, .col-right-check {
      text-align: center;
      .bv-no-focus-ring {
        height: 24px;
      }
      &.disabled {
        filter: grayscale(1);
        opacity: 0.5;
      }
    }
    .col-actions {
      text-align: right;
      white-space: nowrap;
    }
  }
}
</style>

<style lang="scss" scoped>
  .conn-collapse-item {
    margin-bottom: 0.5rem;
    .conn-collapse-button-wrap {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      // background: #f2f2f2;
      border: 1px solid $colorBorderDash;
      padding: 0.5rem 0.5rem;
      border-radius: 8px;
      .conn-collapse-button {
        white-space: nowrap;
        display: flex;
        align-items: center;
        flex: 1;
        font-size: 1rem;
        .status-icon {
          width: 1.5rem; display: inline-block; text-align: left;
        }
        .col-actions {
          text-align: right;
          display: flex;
          align-items: center;
        }
      }
      &.visible {
        background: aquamarine;
      }
    }
    .card {
      border-top: 0;
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }
</style>
